/*
  ResponsiveDialog
    Description:
    This component is a wrapper around the JoyKit Dialog and Drawer components.
    It will render a Drawer on mobile and a Dialog on tablet and desktop to be device responsive.
    The idea is to focus on the content of the dialog/drawer and not the implementation details of the component itself.

    Caveats:
    Behind scenes the Drawer and Dialog components share the same children component, so if you see a Dialog.Body and a Drawer.Body
    they are the same component at the moment. This is because the Drawer and Dialog components are wrappers around a Modal component.

    References:
    This component was inspired by the DashboardDialog created by @fabian-with-joy.
*/
import React from 'react';
import type { DrawerV2Props, DialogV2Props, CloseButtonProps } from '@withjoy/joykit';
import { DialogV2, DrawerV2, CloseButton, Divider } from '@withjoy/joykit';
import { useResponsive } from '@shared/utils/hooks/useResponsive';

import { HeaderWrapper, HeaderLeadingGroup, HeaderLeadingSlot, DialogTitle, drawerOverrides as defaultDrawerOverrides, BodyWrapper } from './ResponsiveDialog.styles';
import { useTranslation } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';

interface ResponsiveDialogProps {
  title?: React.ReactNode;
  /** Rendered inside the header, before the title (e.g. a Back icon button). Additive: absent by
   *  default, so every existing caller's header is unchanged. */
  leadingAction?: React.ReactNode;
  /** `start` (default) keeps Joy's left-aligned title with the leading action beside it. `center`
   *  centres the title in the header and pins the leading action to the left edge, for sheets
   *  whose close control is the modal's own corner X. */
  titleAlign?: 'start' | 'center';
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  drawerOverrides?: DrawerV2Props['overrides'];
  dialogOverrides?: DialogV2Props['overrides'];
  closeButtonOverrides?: CloseButtonProps['overrides'];
  hasCloseButton?: boolean;
  hideDivider?: boolean;
  hideFooterDivider?: boolean;
  renderTitle?: () => React.ReactNode;
  renderFooter?: (onClose: () => void) => React.ReactNode;
  stickyBehavior?: DialogV2Props['stickyBehavior'];
  scrollBehavior?: DialogV2Props['scrollBehavior'];
  disableAutoFocus?: DialogV2Props['disableAutoFocus'];
  disableCloseOnEscapeKeyClick?: DialogV2Props['disableCloseOnEscapeKeyClick'];
  disableCloseOnOutsideClick?: DialogV2Props['disableCloseOnOutsideClick'];
  removeBodyPadding?: boolean;
  overrideBodyProps?: boolean;
  dialogSize?: DialogV2Props['size'];
  setMobileBreakpoints?: Partial<Record<string, boolean>>;
  disableFocusLock?: DialogV2Props['disableFocusLock'];
  disableFocusLockRemoveScroll?: DialogV2Props['disableFocusLockRemoveScroll'];
  initialFocusRef?: DialogV2Props['initialFocusRef'];
  finalFocusRef?: DialogV2Props['finalFocusRef'];
}

export const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
  title,
  leadingAction,
  titleAlign = 'start',
  children,
  isOpen,
  onClose,
  dialogSize,
  drawerOverrides = defaultDrawerOverrides,
  dialogOverrides,
  closeButtonOverrides,
  hasCloseButton = true,
  hideDivider = false,
  hideFooterDivider = false,
  renderTitle,
  renderFooter,
  stickyBehavior,
  scrollBehavior,
  disableAutoFocus,
  disableCloseOnEscapeKeyClick = false,
  disableCloseOnOutsideClick = false,
  removeBodyPadding = false,
  setMobileBreakpoints = { xxs: true, sm: false },
  // When joy-web is embedded inside another app's iframe (e.g. Finola/Carbon),
  // react-focus-lock can't tell "focus moved to the parent window" apart from
  // "focus fell out of the dialog onto the page behind it" and yanks focus
  // back — breaking the parent's chat / other inputs. Default to disabled in
  // embedded contexts; an explicit prop still wins.
  disableFocusLock = isInIframe(),
  disableFocusLockRemoveScroll = false,
  initialFocusRef,
  finalFocusRef,
}) => {
  const [isMobile] = useResponsive({ values: setMobileBreakpoints });

  const { tq } = useTranslation('sharedComponents');

  const content = (
    <>
      {title || hasCloseButton || leadingAction ? (
        <>
          <DialogV2.Header>
            <HeaderWrapper titleAlign={titleAlign}>
              {titleAlign === 'center' ? (
                <>
                  {leadingAction ? <HeaderLeadingSlot>{leadingAction}</HeaderLeadingSlot> : null}
                  <DialogTitle
                    as="h3"
                    $centered
                  >
                    {title}
                  </DialogTitle>
                </>
              ) : (
                <HeaderLeadingGroup>
                  {leadingAction}
                  <DialogTitle as="h3">{title}</DialogTitle>
                </HeaderLeadingGroup>
              )}
              {hasCloseButton && (
                <CloseButton
                  shape="rounded"
                  size="lg"
                  overrides={closeButtonOverrides}
                  onClick={onClose}
                  aria-label={tq`Close`}
                />
              )}
            </HeaderWrapper>
            {renderTitle && renderTitle()}
          </DialogV2.Header>
          {!hideDivider && <Divider />}
        </>
      ) : null}
      <DialogV2.Body {...dialogOverrides?.Body?.props}>
        <BodyWrapper removeBodyPadding={removeBodyPadding}>{children}</BodyWrapper>
      </DialogV2.Body>
      {renderFooter && (
        <>
          {!hideFooterDivider && <Divider />}
          <DialogV2.Footer>{renderFooter(onClose)}</DialogV2.Footer>
        </>
      )}
    </>
  );

  return isMobile ? (
    <DrawerV2
      isOpen={isOpen}
      onClose={onClose}
      anchor="bottom"
      enableReturnFocusOnClose
      useBackdrop
      overrides={drawerOverrides}
      stickyBehavior={stickyBehavior}
      disableAutoFocus={disableAutoFocus}
      disableCloseOnEscapeKeyClick={disableCloseOnEscapeKeyClick}
      disableCloseOnOutsideClick={disableCloseOnOutsideClick}
      disableFocusLock={disableFocusLock}
      disableFocusLockRemoveScroll={disableFocusLockRemoveScroll}
      initialFocusRef={initialFocusRef}
      finalFocusRef={finalFocusRef}
    >
      {content}
    </DrawerV2>
  ) : (
    <DialogV2
      disableCloseOnEscapeKeyClick={disableCloseOnEscapeKeyClick}
      disableCloseOnOutsideClick={disableCloseOnOutsideClick}
      isOpen={isOpen}
      onClose={onClose}
      overrides={dialogOverrides}
      stickyBehavior={stickyBehavior}
      scrollBehavior={scrollBehavior}
      disableAutoFocus={disableAutoFocus}
      size={dialogSize}
      disableFocusLock={disableFocusLock}
      disableFocusLockRemoveScroll={disableFocusLockRemoveScroll}
      initialFocusRef={initialFocusRef}
      finalFocusRef={finalFocusRef}
    >
      {content}
    </DialogV2>
  );
};
